import React from 'react';
import classNames from 'classnames';

interface OverviewSurfaceProps {
  'aria-hidden'?: boolean;
  as?: 'div' | 'section';
  children: React.ReactNode;
  className?: string;
  onClickCapture?: React.MouseEventHandler<HTMLElement>;
  style?: React.CSSProperties;
}

export const OverviewGrid = ({ children }: { children: React.ReactNode }) => (
  <div className="grid w-full grid-cols-1 items-start gap-5 pb-5 lg:grid-cols-[minmax(0,3fr)_minmax(0,6fr)_minmax(0,3fr)]">
    {children}
  </div>
);

export const OverviewColumn = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <div className={classNames('flex min-w-0 flex-col gap-5', className)}>{children}</div>
);

export const OverviewSurface = ({ as: Tag = 'div', children, className, ...rest }: OverviewSurfaceProps) => (
  <Tag
    {...rest}
    className={classNames(
      'flex min-w-0 flex-col !rounded-lg !border !border-solid !border-[#CEDDF2] bg-white',
      className,
    )}
  >
    {children}
  </Tag>
);

export const OverviewCardHeader = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <header className={classNames('flex items-center justify-between gap-3 border-b px-[1.125rem] py-3.5', className)}>
    {children}
  </header>
);

interface OverviewCardProps {
  action?: React.ReactNode;
  children: React.ReactNode;
  subtitle?: React.ReactNode;
  title: string;
}

export const OverviewCard = ({ action, children, subtitle, title }: OverviewCardProps) => (
  <OverviewSurface as="section">
    <OverviewCardHeader className="min-h-[3.25rem] flex-wrap rounded-t-[7px] border-[#E1EBFA] bg-gradient-to-b from-[#F3F9FF] to-white">
      <div className="flex min-w-0 flex-col gap-1">
        <h2 className="m-0 min-w-0 text-base font-bold leading-[1.3] tracking-[-0.01em] text-[#192940]">{title}</h2>
        {subtitle}
      </div>
      {action ? <div className="ml-auto shrink-0">{action}</div> : null}
    </OverviewCardHeader>
    <div className="px-[1.125rem] pb-4 pt-4">{children}</div>
  </OverviewSurface>
);

export const OverviewMetrics = ({ children }: { children: React.ReactNode }) => (
  <div className="[&>div]:!border-[#E1EBFA] [&>div]:!py-[0.5625rem] [&>div]:text-[0.8125rem] [&_.label]:text-[#5B7292] [&_.value]:font-semibold [&_.value]:tabular-nums [&_.value]:text-[#192940]">
    {children}
  </div>
);

interface OverviewKeyDataProps {
  action?: React.ReactNode;
  metrics: { label: string; value: string }[];
  title: string;
}

export const OverviewKeyData = ({ action, metrics, title }: OverviewKeyDataProps) =>
  metrics.length > 0 ? (
    <OverviewCard action={action} title={title}>
      <OverviewMetrics>
        {metrics.map(metric => (
          <div className="flex w-full items-center justify-between border-b last:border-b-0" key={metric.label}>
            <div className="label">{metric.label}</div>
            <div className="value text-right">{metric.value}</div>
          </div>
        ))}
      </OverviewMetrics>
    </OverviewCard>
  ) : null;
